// React
import React, { useEffect, useState } from 'react';

// Material UI
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import { RootStore } from '../../../Store';
import { getAllPreorderEvents } from '../../../actions/preorderEventActions/PreorderEventActions';

// Components
import Pagination from '../utils/ResultsPagination';

// Config
import { getProjectConfig } from '../../../getProjectConfig';

// Translations
import { useTranslation } from 'react-i18next';

// Styles
import useStyles from './PreordersRowStyles';

interface PreordersRowProps {
  hasSearched: boolean;
  onSelectEvent: (eventId: number) => void;
}

const PreordersRow: React.FC<PreordersRowProps> = ({ hasSearched, onSelectEvent }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('global');

  const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
  const { preorderEventList, preorderEventFilters, loading } = preorderEventState;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const url = window.location.href;

  // Fetch data only after a search has been performed
  useEffect(() => {
    if (!hasSearched) return;

    const params = new URLSearchParams(window.location.search);
    let page = params.get('page');
    const pNumber = Number(page);

    const baseFilters = {
      name: preorderEventFilters?.name,
      releaseDate: preorderEventFilters?.releaseDate,
      lastPreorderDate: preorderEventFilters?.lastPreorderDate
    };

    if (page && page !== null) {
      const qParams = {
        ...baseFilters,
        page: pNumber - 1,
        size: getProjectConfig().PAGES_SIZE,
        paged: true,
      };
      dispatch(getAllPreorderEvents(qParams));
      setCurrentPage(pNumber);
    } else {
      const qParams = {
        ...baseFilters,
        page: 0,
        size: getProjectConfig().PAGES_SIZE,
        paged: true,
      };
      dispatch(getAllPreorderEvents(qParams));
      setCurrentPage(1);
    }
    // eslint-disable-next-line
  }, [url, hasSearched, preorderEventFilters]);

  if (!hasSearched) {
    return (
      <div className={classes.root}>
        <Typography variant="body1" color="textSecondary">
          {t('preorders.no-results')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {loading === 'GET_PREORDER_EVENTS' && <CircularProgress size={26} color="primary" />}
      {preorderEventList?.content && preorderEventList.content.length > 0 ? (
        <>
          {preorderEventList.content.map(event => (
            <Grid container spacing={2} key={event.id} className={classes.row}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography className={classes.name}>{event.name}</Typography>
                <Typography variant="caption">ID: {event.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                <Typography>{t('preorders.release-date')}: {event.releaseDate || '-'}</Typography>
                <Typography>{t('preorders.last-preorder-date')}: {event.lastPreorderDate || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={3} md={3}>
                {event.archived && <Chip label={t('common.archived')} size="small" color="secondary" className={classes.statusChip} />}
                {event.closed && <Chip label={t('common.closed')} size="small" color="primary" className={classes.statusChip} />}
                {!event.archived && !event.closed && <Chip label={t('common.active')} size="small" className={classes.statusChip} />}
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Link
                  component="button"
                  color="primary"
                  onClick={() => onSelectEvent(event.id)}
                  className={classes.linkButton}
                >
                  {t('common.view-details')}
                </Link>
              </Grid>
            </Grid>
          ))}
          {preorderEventList?.totalPages && preorderEventList.totalPages > 1 && (
            <div className={classes.pagination}>
              <Pagination current={currentPage} path="/admin/preorders" pagesNumber={preorderEventList.totalPages} />
            </div>
          )}
        </>
      ) : (
        <Typography variant="body1" color="textSecondary">{t('preorders.no-results')}</Typography>
      )}
    </div>
  );
};

export default PreordersRow;
